// Curriculum Intelligence Config — Board-aware class/subject/chapter mappings
// Supports: CBSE, ICSE, IGCSE, IB, CIS, State Boards

export const BOARDS = [
  { id: 'cbse', label: 'CBSE', country: 'India' },
  { id: 'icse', label: 'ICSE', country: 'India' },
  { id: 'igcse', label: 'IGCSE (Cambridge)', country: 'International' },
  { id: 'ib', label: 'IB', country: 'International' },
  { id: 'cis', label: 'CIS', country: 'International' },
  { id: 'state_board', label: 'State Board', country: 'India' },
];

export const TEACHING_GOALS = [
  { id: 'syllabus_completion', label: 'Syllabus Completion', icon: 'BookOpen' },
  { id: 'exam_preparation', label: 'Exam Preparation', icon: 'GraduationCap' },
  { id: 'concept_reinforcement', label: 'Concept Reinforcement', icon: 'Lightbulb' },
  { id: 'activity_engagement', label: 'Activity / Engagement', icon: 'Sparkles' },
];

export const PLANNING_SCOPES = [
  { id: 'single_class', label: 'Single Class' },
  { id: 'lesson_kit', label: 'Full Lesson (Lesson Kit)' },
];

export const TEACHING_OBJECTIVES = [
  { id: 'concept_clarity', label: 'Concept Clarity' },
  { id: 'practice', label: 'Practice' },
  { id: 'assessment', label: 'Assessment' },
  { id: 'skill_building', label: 'Skill Building' },
];

export const TEACHING_STYLES = [
  { id: 'exam_focused', label: 'Exam-focused (CBSE/ICSE)', boards: ['cbse', 'icse', 'state_board'] },
  { id: 'conceptual', label: 'Conceptual (IB)', boards: ['ib', 'cis'] },
  { id: 'application_based', label: 'Application-based (IGCSE)', boards: ['igcse'] },
  { id: 'state_board', label: 'State Board Pattern', boards: ['state_board'] },
  { id: 'balanced', label: 'Balanced Approach', boards: [] },
];

export const DIFFICULTY_LEVELS = [
  { id: 'easy', label: 'Easy', pct: 30 },
  { id: 'medium', label: 'Medium', pct: 50 },
  { id: 'hard', label: 'Hard', pct: 20 },
];

export const DURATIONS = [30, 45, 60, 90];

export const MICRO_ACTIONS = [
  { id: 'simplify', label: 'Simplify', icon: 'ArrowDown' },
  { id: 'increase_complexity', label: 'Increase Complexity', icon: 'ArrowUp' },
  { id: 'add_questions', label: 'Add Questions', icon: 'Plus' },
  { id: 'convert_format', label: 'Convert Format', icon: 'RefreshCw' },
];

// Subject → Chapter mappings by board (sample for CBSE; extensible)
export const CURRICULUM_CHAPTERS = {
  cbse: {
    Mathematics: {
      '10': ['Real Numbers', 'Polynomials', 'Linear Equations', 'Quadratic Equations', 'Arithmetic Progressions', 'Triangles', 'Coordinate Geometry', 'Trigonometry', 'Areas Related to Circles', 'Surface Areas & Volumes', 'Statistics', 'Probability'],
      '9': ['Number Systems', 'Polynomials', 'Coordinate Geometry', 'Linear Equations in Two Variables', 'Euclids Geometry', 'Lines and Angles', 'Triangles', 'Quadrilaterals', 'Circles', 'Heron Formula', 'Surface Areas & Volumes', 'Statistics', 'Probability'],
      '12': ['Relations and Functions', 'Inverse Trigonometric Functions', 'Matrices', 'Determinants', 'Continuity and Differentiability', 'Application of Derivatives', 'Integrals', 'Application of Integrals', 'Differential Equations', 'Vector Algebra', 'Three Dimensional Geometry', 'Linear Programming', 'Probability'],
      '11': ['Sets', 'Relations and Functions', 'Trigonometric Functions', 'Complex Numbers', 'Linear Inequalities', 'Permutations and Combinations', 'Binomial Theorem', 'Sequences and Series', 'Straight Lines', 'Conic Sections', 'Limits and Derivatives', 'Statistics', 'Probability'],
    },
    Science: {
      '10': ['Chemical Reactions', 'Acids Bases and Salts', 'Metals and Non-metals', 'Carbon Compounds', 'Life Processes', 'Control and Coordination', 'Heredity and Evolution', 'Light', 'Human Eye', 'Electricity', 'Magnetic Effects', 'Sources of Energy', 'Environment', 'Sustainable Development'],
      '9': ['Matter in Our Surroundings', 'Is Matter Around Us Pure', 'Atoms and Molecules', 'Structure of Atom', 'The Fundamental Unit of Life', 'Tissues', 'Motion', 'Force and Laws of Motion', 'Gravitation', 'Work and Energy', 'Sound', 'Improvement in Food Resources'],
    },
    English: {
      '10': ['A Letter to God', 'Nelson Mandela', 'Two Stories about Flying', 'From the Diary of Anne Frank', 'Glimpses of India', 'A Tiger in the Zoo', 'The Ball Poem', 'Amanda', 'Animals', 'The Tale of Custard the Dragon', 'The Proposal'],
      '9': ['The Fun They Had', 'The Sound of Music', 'The Little Girl', 'A Truly Beautiful Mind', 'The Snake and the Mirror', 'My Childhood', 'Reach for the Top', 'Kathmandu', 'If I Were You', 'The Road Not Taken', 'Wind', 'Rain on the Roof'],
    },
    Physics: {
      '12': ['Electric Charges and Fields', 'Electrostatic Potential', 'Current Electricity', 'Moving Charges and Magnetism', 'Magnetism and Matter', 'Electromagnetic Induction', 'Alternating Current', 'Electromagnetic Waves', 'Ray Optics', 'Wave Optics', 'Dual Nature of Radiation', 'Atoms', 'Nuclei', 'Semiconductor Electronics'],
      '11': ['Physical World', 'Units and Measurements', 'Motion in a Straight Line', 'Motion in a Plane', 'Laws of Motion', 'Work Energy Power', 'System of Particles', 'Gravitation', 'Mechanical Properties of Solids', 'Mechanical Properties of Fluids', 'Thermal Properties of Matter', 'Thermodynamics', 'Kinetic Theory', 'Oscillations', 'Waves'],
    },
    Chemistry: {
      '12': ['The Solid State', 'Solutions', 'Electrochemistry', 'Chemical Kinetics', 'Surface Chemistry', 'Isolation of Elements', 'p-Block Elements', 'd and f Block Elements', 'Coordination Compounds', 'Haloalkanes and Haloarenes', 'Alcohols Phenols Ethers', 'Aldehydes Ketones', 'Amines', 'Biomolecules', 'Polymers'],
      '11': ['Some Basic Concepts', 'Structure of Atom', 'Classification of Elements', 'Chemical Bonding', 'Thermodynamics', 'Equilibrium', 'Redox Reactions', 'Hydrogen', 'The s-Block Elements', 'The p-Block Elements', 'Organic Chemistry Basics', 'Hydrocarbons', 'Environmental Chemistry'],
    },
    Biology: {
      '12': ['Reproduction in Organisms', 'Sexual Reproduction in Flowering Plants', 'Human Reproduction', 'Reproductive Health', 'Principles of Inheritance', 'Molecular Basis of Inheritance', 'Evolution', 'Human Health and Disease', 'Strategies for Food Enhancement', 'Microbes in Human Welfare', 'Biotechnology Principles', 'Biotechnology Applications', 'Organisms and Populations', 'Ecosystem', 'Biodiversity', 'Environmental Issues'],
      '11': ['The Living World', 'Biological Classification', 'Plant Kingdom', 'Animal Kingdom', 'Morphology of Flowering Plants', 'Anatomy of Flowering Plants', 'Structural Organisation in Animals', 'Cell', 'Biomolecules', 'Cell Cycle', 'Photosynthesis', 'Respiration', 'Plant Growth', 'Breathing and Exchange of Gases', 'Body Fluids', 'Excretory Products', 'Locomotion and Movement', 'Neural Control', 'Chemical Coordination'],
    },
    'Social Studies': {
      '10': ['The Rise of Nationalism in Europe', 'Nationalism in India', 'The Making of a Global World', 'The Age of Industrialisation', 'Resources and Development', 'Forest and Wildlife Resources', 'Water Resources', 'Agriculture', 'Minerals and Energy Resources', 'Manufacturing Industries', 'Power Sharing', 'Federalism', 'Democracy and Diversity', 'Gender Religion and Caste', 'Popular Struggles and Movements', 'Political Parties', 'Development', 'Money and Credit', 'Globalisation'],
    },
    Hindi: {
      '10': ['Sakhi', 'Ram-Lakshman-Parashuram Samvad', 'Atmaparichay', 'Bade Bhai Sahab', 'Diary Ka Ek Panna', 'Tatara Vamiro Katha', 'Netaji Ka Chasma', 'Balgobin Bhagat', 'Lakhnavi Andaz', 'Naubatkhane mein Ibadat'],
    },
    'Computer Science': {
      '12': ['Python Revision', 'Functions', 'File Handling', 'Data Structures', 'Computer Networks', 'Database Concepts', 'SQL', 'Boolean Algebra', 'Communication Technologies'],
      '11': ['Computer Systems', 'Encoding Schemes', 'Python Fundamentals', 'Data Types', 'Flow of Control', 'Functions', 'Strings', 'Lists', 'Tuples', 'Dictionaries', 'Societal Impacts'],
    },
  },
  icse: {}, igcse: {}, ib: {}, cis: {}, state_board: {},
};

export const getChaptersForBoard = (board, subject, classId) => {
  const boardData = CURRICULUM_CHAPTERS[board] || CURRICULUM_CHAPTERS['cbse'];
  const subjectData = boardData[subject];
  if (!subjectData) return [];
  return subjectData[classId] || [];
};
